import { ReactElement, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { useDispatch } from 'hooks';
import { groupBy, sortBy, values } from 'lodash';
import { useTranslation } from 'react-i18next';
import { fetchHolidays } from 'redux/actionCreators';

import { PublicationInfo, RosterDay } from './types';
import { Interval, PlanUnit } from 'components/types';

import { formatISOString } from 'utils/date';

import { getLatest } from 'RestAPIs/cost-center/publication';

import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';

import { Select } from 'components/shared';
import DateInterval from 'components/shared/DateInterval';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';

import HeaderTableCell from './HeaderTableCell';
import { Icon } from '@sdb/planning-ui';
import { StyledRosterViewTableBox, HeaderStyles } from './Header.styles';

type Props = {
  lastSelectedPlanUnitsIds?: (string | number)[] | null;
  onPlanUnitSelected: (selectedPlanUnits: PlanUnit[]) => void;
  onPrintRoster: () => void;
  interval: Interval;
  onIntervalChanged: (interval: Interval) => void;
  rosterDays: RosterDay[];
  calendarView: 'week' | 'month';
  rosterView: 'employees' | 'duties';
  onRosterViewChanged?: (rosterView: 'employees' | 'duties') => void;
  children: ReactElement | ReactElement[];
};

export default function Header(props: Props) {
  const {
    lastSelectedPlanUnitsIds,
    onPlanUnitSelected,
    onPrintRoster,
    interval,
    onIntervalChanged,
    rosterDays,
    calendarView,
    rosterView,
    onRosterViewChanged,
    children
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const [publicationInfo, setPublicationInfo] =
    useState<PublicationInfo | null>(null);

  const loadPublicationInfo = (value: PlanUnit[]) => {
    getLatest(value.map(({ id }) => id)).then(({ data }) => {
      setPublicationInfo(data);
    });
  };

  const onSelectedPlanUnit = (value: PlanUnit[]) => {
    setPublicationInfo(null);
    if (value.length === 1) {
      loadPublicationInfo(value);
    }
    onPlanUnitSelected(value);
  };

  const weeks = sortBy(
    values(groupBy(rosterDays, 'weekNr')),
    (arr) => arr[0].date
  );

  const minDate = dayjs('2015-01-01').toDate();
  const maxDate = dayjs().add(2, 'year').toDate();

  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  return (
    <HeaderStyles>
      <div className="main-nav d-flex v-center between">
        <div className="side-container d-flex v-bottom">
          <PlanUnitSelector
            hideGroups
            onSelected={onSelectedPlanUnit}
            interval={interval}
            doNotPersist
            useEmployeePlanunits
            defaultValue={
              lastSelectedPlanUnitsIds?.map(
                (uniqId) =>
                  ({
                    uniqId
                  }) as PlanUnit
              ) || undefined
            }
          />
        </div>
        <DateInterval
          defaultInterval={interval}
          onSelected={onIntervalChanged}
          view={calendarView}
          minDate={minDate}
          maxDate={maxDate}
        />
        <div className="side-container d-flex end v-center">
          <Tooltip
            title={
              <div>
                <div>
                  {publicationInfo?.lastPublishDate && (
                    <>
                      <span
                        style={{ width: 110, display: 'inline-block' }}
                        className="publication-info"
                      >
                        <strong>{t('publishedUntil')}</strong>:{' '}
                      </span>
                      {formatISOString(publicationInfo.lastPublishDate)}
                    </>
                  )}
                  {!publicationInfo?.lastPublishDate && t('rosterNotPublished')}
                </div>
                <div>
                  {publicationInfo?.visibleUntilDate && (
                    <>
                      <span
                        style={{ width: 110, display: 'inline-block' }}
                        className="publication-info"
                      >
                        <strong>{t('visibleUntil')}</strong>:{' '}
                      </span>
                      {formatISOString(publicationInfo.visibleUntilDate)}
                    </>
                  )}
                  {!publicationInfo?.visibleUntilDate && t('visibilityNotSet')}
                </div>
              </div>
            }
            arrow
          >
            <IconButton size="small" color="primary">
              <Icon type="eye" fontSize="small" color="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('printRoster') || ''} arrow>
            <IconButton
              onClick={onPrintRoster}
              size="small"
              color="primary"
              className="print-roster-btn"
            >
              <Icon type="print" fontSize="small" color="inherit" />
            </IconButton>
          </Tooltip>
          {onRosterViewChanged && (
            <Select
              label={t('view')}
              onSelected={onRosterViewChanged}
              options={[
                {
                  value: 'employees',
                  label: t('employees')
                },
                {
                  value: 'duties',
                  label: t('duties')
                }
              ]}
              valueProp={rosterView}
              className="mode-selector"
            />
          )}
        </div>
      </div>
      <StyledRosterViewTableBox>
        <Table className="roster-grid">
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} className="filters">
                {children}
              </TableCell>
              {calendarView === 'month' &&
                weeks.map((week) => (
                  <TableCell key={week[0].weekNr} colSpan={week.length}>
                    <div>{`${t('week')} ${week[0].weekNr}`}</div>
                  </TableCell>
                ))}
            </TableRow>
            <TableRow>
              {rosterDays.map((rosterDay) => (
                <HeaderTableCell
                  rosterDay={rosterDay}
                  key={+rosterDay.date}
                  view={calendarView}
                />
              ))}
            </TableRow>
          </TableHead>
        </Table>
      </StyledRosterViewTableBox>
    </HeaderStyles>
  );
}
